/* eslint-disable no-unused-vars */

import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";

import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Tooltip from "@material-ui/core/Tooltip";
import Chip from "@material-ui/core/Chip";

import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import DescriptionIcon from "@material-ui/icons/Description";
import PlayCircleOutlineIcon from "@material-ui/icons/PlayCircleOutline";
import PauseCircleOutlineIcon from "@material-ui/icons/PauseCircleOutline";
import RepeatIcon from "@material-ui/icons/Repeat";
import StopIcon from "@material-ui/icons/Stop";
import ScheduleIcon from "@material-ui/icons/Schedule";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import PageHelpGuide from "../../components/PageHelpGuide";
import { CAMPAIGNS_HELP_DATA } from "../../constants/pageHelp/campaignsHelp";
import { getStandardButtonStyles } from "../../styles/standardButtons";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import CampaignModal from "../../components/CampaignModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { Grid, FormControl, InputLabel, Select, MenuItem, TablePagination, Pagination, Box, TableContainer, TableSortLabel } from "@material-ui/core";
import { isArray } from "lodash";
import { useDate } from "../../hooks/useDate";
import ForbiddenPage from "../../components/ForbiddenPage";
import usePlans from "../../hooks/usePlans";
import { AuthContext } from "../../context/Auth/AuthContext";

const reducer = (state, action) => {
  if (action.type === "LOAD_CAMPAIGNS") {
    const campaigns = action.payload;
    const newCampaigns = [];

    if (isArray(campaigns)) {
      campaigns.forEach((campaign) => {
        const campaignIndex = state.findIndex((u) => u.id === campaign.id);
        if (campaignIndex !== -1) {
          state[campaignIndex] = campaign;
        } else {
          newCampaigns.push(campaign);
        }
      });
    }

    return [...state, ...newCampaigns];
  }

  if (action.type === "UPDATE_CAMPAIGNS") {
    const campaign = action.payload;
    const campaignIndex = state.findIndex((u) => u.id === campaign.id);

    if (campaignIndex !== -1) {
      state[campaignIndex] = campaign;
      return [...state];
    } else {
      return [campaign, ...state];
    }
  }

  if (action.type === "DELETE_CAMPAIGN") {
    const campaignId = action.payload;

    const campaignIndex = state.findIndex((u) => u.id === campaignId);
    if (campaignIndex !== -1) {
      state.splice(campaignIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  ...getStandardButtonStyles(theme),
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderRadius: 16,
    backgroundColor: theme.palette.type === 'dark' ? '#2A2D32 !important' : theme.palette.background.paper,
  },
  scrollArea: {
    flex: 1,
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  recurringChip: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    fontSize: '0.75rem',
  },
  statusChip: {
    fontSize: '0.75rem',
  },
  nextExecutionCell: {
    fontWeight: 500,
    color: theme.palette.text.secondary,
  },
  filterContainer: {
    marginBottom: theme.spacing(2),
  },
  tableHeader: {
    fontWeight: 'bold',
    backgroundColor: "#1e1e1e",
    color: "#fff",
    borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
  },
}));

const Campaigns = () => {
  const classes = useStyles();
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [deletingCampaign, setDeletingCampaign] = useState(null);
  const [campaignModalOpen, setCampaignModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [stopRecurrenceModalOpen, setStopRecurrenceModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [recurrenceFilter, setRecurrenceFilter] = useState("");
  const [sortBy, setSortBy] = useState("scheduledAt_DESC");
  const [campaigns, dispatch] = useReducer(reducer, []);
  const { user, socket } = useContext(AuthContext);

  const { datetimeToClient } = useDate();
  const { getPlanCompany } = usePlans();

  useEffect(() => {
    async function fetchData() {
      const companyId = user.companyId;
      const planConfigs = await getPlanCompany(undefined, companyId);
      if (!planConfigs.plan?.useCampaigns) {
        toast.error(i18n.t("toastError.permissionDenied"));
        setTimeout(() => {
          history.push(`/`)
        }, 1000);
      }
    }
    fetchData();
  }, []);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam, statusFilter, recurrenceFilter, pageSize]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchCampaigns();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber, statusFilter, recurrenceFilter, pageSize, sortBy]);

  useEffect(() => {
    const companyId = user.companyId;

    const onCompanyCampaign = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_CAMPAIGNS", payload: data.record });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_CAMPAIGN", payload: +data.id });
      }
    }

    socket.on(`company-${companyId}-campaign`, onCompanyCampaign);
    return () => {
      socket.off(`company-${companyId}-campaign`, onCompanyCampaign);
    };
  }, [user]);

  const fetchCampaigns = async () => {
    try {
      const { data } = await api.get("/campaigns/", {
        params: {
          searchParam,
          pageNumber,
          pageSize,
          status: statusFilter,
          isRecurring: recurrenceFilter,
          sortBy
        },
      });

      // Para paginação real, substituir os dados em vez de adicionar
      if (pageNumber === 1) {
        dispatch({ type: "RESET" });
      }
      dispatch({ type: "LOAD_CAMPAIGNS", payload: data.records });

      setHasMore(data.hasMore);
      setTotalPages(data.totalPages || 1);
      setTotalCount(data.count || 0);
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenCampaignModal = () => {
    setSelectedCampaign(null);
    setCampaignModalOpen(true);
  };

  const handleCloseCampaignModal = () => {
    setSelectedCampaign(null);
    setCampaignModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
  };

  const handleRecurrenceFilterChange = (event) => {
    setRecurrenceFilter(event.target.value);
  };


  const handleDuplicateCampaign = (campaign) => {
    const duplicate = {
      ...campaign,
      id: undefined,
      name: `${campaign.name} (Cópia)`,
      status: "INATIVA",
      scheduledAt: "",
      completedAt: "",
      mediaPath: null,
      mediaName: null
    };
    setSelectedCampaign(duplicate);
    setCampaignModalOpen(true);
  };

  const handleEditCampaign = (campaign) => {

    setSelectedCampaign(campaign);
    setCampaignModalOpen(true);
  };

  const handleDeleteCampaign = async (campaignId) => {
    try {
      await api.delete(`/campaigns/${campaignId}`);
      toast.success(i18n.t("campaigns.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingCampaign(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const handleStopRecurrence = async (campaignId) => {
    try {
      await api.post(`/campaigns/${campaignId}/stop-recurrence`);
      toast.success(i18n.t("campaigns.toasts.stopRecurrence"));
      setPageNumber(1);
      fetchCampaigns();
    } catch (err) {
      toastError(err);
    }
    setStopRecurrenceModalOpen(false);
    setSelectedCampaign(null);
  };

  const handlePageChange = (event, newPage) => {
    setPageNumber(newPage + 1); // Material-UI usa índice baseado em 0
  };

  const handlePageSizeChange = (event) => {
    setPageSize(parseInt(event.target.value, 10));
    setPageNumber(1);
  };

  const formatStatus = (val) => {
    switch (val) {
      case "INATIVA":
        return i18n.t("campaigns.status.INATIVA");
      case "PROGRAMADA":
        return i18n.t("campaigns.status.PROGRAMADA");
      case "EM_ANDAMENTO":
        return i18n.t("campaigns.status.EM_ANDAMENTO");
      case "CANCELADA":
        return i18n.t("campaigns.status.CANCELADA");
      case "FINALIZADA":
        return i18n.t("campaigns.status.FINALIZADA");
      default:
        return val;
    }
  };

  const getStatusColor = (status) => {
    if (status === "CANCELADA") return "secondary";
    if (status === "INATIVA") return "default";
    if (status === "PROGRAMADA") return "primary";
    if (status === "EM_ANDAMENTO") return "primary";
    if (status === "FINALIZADA") return "default";
    return "primary";
  };

  // ...

  const formatRecurrenceType = (type) => {
    switch (type) {
      case "minutely":
        return i18n.t("campaigns.recurrence.minutely");
      case "hourly":
        return i18n.t("campaigns.recurrence.hourly");
      case "daily":
        return i18n.t("campaigns.recurrence.daily");
      case "weekly":
        return i18n.t("campaigns.recurrence.weekly");
      case "biweekly":
        return i18n.t("campaigns.recurrence.biweekly");
      case "monthly":
        return i18n.t("campaigns.recurrence.monthly");
      case "yearly":
        return i18n.t("campaigns.recurrence.yearly");
      default:
        return type;
    }
  };

  const cancelCampaign = async (campaign) => {
    try {
      await api.post(`/campaigns/${campaign.id}/cancel`);
      toast.success(i18n.t("campaigns.toasts.cancel"));
      setPageNumber(1);
      fetchCampaigns();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const restartCampaign = async (campaign) => {
    try {
      await api.post(`/campaigns/${campaign.id}/restart`);
      toast.success(i18n.t("campaigns.toasts.restart"));
      setPageNumber(1);
      fetchCampaigns();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          deletingCampaign &&
          `${i18n.t("campaigns.confirmationModal.deleteTitle")} ${deletingCampaign.name}?`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteCampaign(deletingCampaign.id)}
      >
        {i18n.t("campaigns.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      <ConfirmationModal
        title={i18n.t("campaigns.confirmationModal.stopRecurrenceTitle")}
        open={stopRecurrenceModalOpen}
        onClose={() => setStopRecurrenceModalOpen(false)}
        onConfirm={() => handleStopRecurrence(selectedCampaign?.id)}
      >
        {i18n.t("campaigns.confirmationModal.stopRecurrenceMsg")}
      </ConfirmationModal>

      {
        campaignModalOpen && (
          <CampaignModal
            resetPagination={() => {
              setPageNumber(1);
              fetchCampaigns();
            }}
            open={campaignModalOpen}
            onClose={handleCloseCampaignModal}
            aria-labelledby="form-dialog-title"
            campaignId={selectedCampaign && selectedCampaign.id}
            initialValues={selectedCampaign && !selectedCampaign.id ? selectedCampaign : undefined}
          />
        )
      }
      {
        user.profile === "user" && user?.showCampaign === "disabled" ?
          <ForbiddenPage />
          :
          <>
            <Paper className={classes.mainPaper} variant="outlined">
              <MainHeader>
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <PageHelpGuide
                    helpData={CAMPAIGNS_HELP_DATA}
                    tooltipText="Guía y Ayuda de Campañas (Haz clic para ver)"
                  />
                  <Title>{i18n.t("campaigns.subMenus.list")}</Title>
                </div>
                <MainHeaderButtonsWrapper>
                  <TextField
                    placeholder={i18n.t("campaigns.searchPlaceholder")}
                    type="search"
                    value={searchParam}
                    onChange={handleSearch}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon color="secondary" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Button
                    variant="contained"
                    className={classes.applyButton}
                    onClick={handleOpenCampaignModal}
                  >
                    {i18n.t("campaigns.buttons.add")}
                  </Button>
                </MainHeaderButtonsWrapper>
              </MainHeader>

              {/* Filtros */}
              <Box style={{ padding: "0 20px 16px 20px" }}>
                <Grid spacing={2} container>
                  <Grid xs={12} sm={4} item>
                    <FormControl fullWidth variant="outlined" size="small">
                      <InputLabel>{i18n.t("campaigns.filters.status")}</InputLabel>
                      <Select
                        value={statusFilter}
                        onChange={handleStatusFilterChange}
                        label={i18n.t("campaigns.filters.status")}
                      >
                        <MenuItem value="">{i18n.t("campaigns.filters.all")}</MenuItem>
                        <MenuItem value="INATIVA">{i18n.t("campaigns.status.INATIVA")}</MenuItem>
                        <MenuItem value="PROGRAMADA">{i18n.t("campaigns.status.PROGRAMADA")}</MenuItem>
                        <MenuItem value="EM_ANDAMENTO">{i18n.t("campaigns.status.EM_ANDAMENTO")}</MenuItem>
                        <MenuItem value="CANCELADA">{i18n.t("campaigns.status.CANCELADA")}</MenuItem>
                        <MenuItem value="FINALIZADA">{i18n.t("campaigns.status.FINALIZADA")}</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid xs={12} sm={4} item>
                    <FormControl fullWidth variant="outlined" size="small">
                      <InputLabel>{i18n.t("campaigns.filters.recurrence")}</InputLabel>
                      <Select
                        value={recurrenceFilter}
                        onChange={handleRecurrenceFilterChange}
                        label={i18n.t("campaigns.filters.recurrence")}
                      >
                        <MenuItem value="">{i18n.t("campaigns.filters.all")}</MenuItem>
                        <MenuItem value="true">{i18n.t("campaigns.recurrence.recurring")}</MenuItem>
                        <MenuItem value="false">{i18n.t("campaigns.recurrence.unique")}</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>

              <TableContainer className={classes.scrollArea}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell align="center" className={classes.tableHeader}>
                      <TableSortLabel
                        active={sortBy.startsWith("name")}
                        direction={sortBy === "name_ASC" ? "asc" : "desc"}
                        onClick={() => {
                          setSortBy(sortBy === "name_ASC" ? "name_DESC" : "name_ASC");
                        }}
                      >
                        {i18n.t("campaigns.table.name")}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="center" className={classes.tableHeader}>
                      {i18n.t("campaigns.table.status")}
                    </TableCell>
                    <TableCell align="center" className={classes.tableHeader}>
                      {i18n.t("campaigns.table.recurrence")}
                    </TableCell>
                    <TableCell align="center" className={classes.tableHeader}>
                      {i18n.t("campaigns.table.contactList")}
                    </TableCell>
                    <TableCell align="center" className={classes.tableHeader}>
                      {i18n.t("campaigns.table.whatsapp")}
                    </TableCell>
                    <TableCell align="center" className={classes.tableHeader}>
                      <TableSortLabel
                        active={sortBy.startsWith("scheduledAt")}
                        direction={sortBy === "scheduledAt_ASC" ? "asc" : "desc"}
                        onClick={() => {
                          setSortBy(sortBy === "scheduledAt_ASC" ? "scheduledAt_DESC" : "scheduledAt_ASC");
                        }}
                      >
                        {i18n.t("campaigns.table.scheduledAt")}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="center" className={classes.tableHeader}>
                      {i18n.t("campaigns.table.nextExecution")}
                    </TableCell>
                    <TableCell align="center" className={classes.tableHeader}>
                      {i18n.t("campaigns.table.completedAt")}
                    </TableCell>
                    <TableCell align="center" className={classes.tableHeader}>
                      {i18n.t("campaigns.table.confirmation")}
                    </TableCell>
                    <TableCell align="center" className={classes.tableHeader}>
                      {i18n.t("campaigns.table.actions")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <>
                    {campaigns.map((campaign) => (
                      <TableRow key={campaign.id}>
                        <TableCell align="center">
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                            {campaign.isRecurring && (
                              <Tooltip title={i18n.t("campaigns.recurrence.recurring")}>
                                <RepeatIcon color="primary" fontSize="small" />
                              </Tooltip>
                            )}
                            {campaign.name}
                          </div>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={formatStatus(campaign.status)}
                            color={getStatusColor(campaign.status)}
                            size="small"
                            className={classes.statusChip}
                          />
                        </TableCell>
                        <TableCell align="center">
                          {campaign.isRecurring ? (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                              <Chip
                                label={formatRecurrenceType(campaign.recurrenceType)}
                                className={classes.recurringChip}
                                size="small"
                              />
                              <span style={{ fontSize: '0.75rem', color: '#666' }}>
                                {i18n.t("campaigns.recurrence.executions", { count: campaign.executionCount || 0 })}
                              </span>
                            </div>
                          ) : (
                            <Chip
                              label={i18n.t("campaigns.recurrence.unique")}
                              variant="outlined"
                              size="small"
                              className={classes.statusChip}
                            />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {campaign.contactListId
                            ? campaign.contactList?.name || i18n.t("campaigns.table.contactList") // Fallback or key?
                            : campaign.tagListId && campaign.tagListId !== "Nenhuma"
                              ? `Tag: ${campaign.tagListId}`
                              : i18n.t("campaigns.settings.undefined")}
                        </TableCell>
                        <TableCell align="center">
                          {campaign.whatsappId && campaign.whatsapp
                            ? campaign.whatsapp.name
                            : i18n.t("campaigns.settings.undefined")}
                        </TableCell>
                        <TableCell align="center">
                          {campaign.scheduledAt ? datetimeToClient(campaign.scheduledAt) : "—"}
                        </TableCell>
                        <TableCell align="center" className={classes.nextExecutionCell}>
                          {campaign.isRecurring && campaign.nextScheduledAt ? (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                              <ScheduleIcon fontSize="small" color="primary" />
                              {datetimeToClient(campaign.nextScheduledAt)}
                            </div>
                          ) : campaign.isRecurring && campaign.status === 'FINALIZADA' ? (
                            <span style={{ color: '#666', fontSize: '0.875rem' }}>
                              {i18n.t("campaigns.recurrence.finished")}
                            </span>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {campaign.completedAt ? datetimeToClient(campaign.completedAt) : "—"}
                        </TableCell>
                        <TableCell align="center">
                          {campaign.confirmation ? i18n.t("campaigns.table.enabled") : i18n.t("campaigns.table.disabled")}
                        </TableCell>
                        <TableCell align="center">
                          <div style={{ display: 'flex', justifyContent: 'center', gap: 4 }}>
                            {campaign.status === "EM_ANDAMENTO" && (
                              <Tooltip title={i18n.t("campaigns.tooltips.stop")}>
                                <IconButton
                                  onClick={() => cancelCampaign(campaign)}
                                  size="small"
                                >
                                  <PauseCircleOutlineIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                            {campaign.status === "CANCELADA" && (
                              <Tooltip title={i18n.t("campaigns.tooltips.restart")}>
                                <IconButton
                                  onClick={() => restartCampaign(campaign)}
                                  size="small"
                                >
                                  <PlayCircleOutlineIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                            {campaign.isRecurring && campaign.status !== "FINALIZADA" && (
                              <Tooltip title={i18n.t("campaigns.confirmationModal.stopRecurrenceTitle")}>
                                <IconButton
                                  onClick={() => {
                                    setSelectedCampaign(campaign);
                                    setStopRecurrenceModalOpen(true);
                                  }}
                                  size="small"
                                  color="secondary"
                                >
                                  <StopIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title={i18n.t("campaigns.tooltips.report")}>
                              <IconButton
                                onClick={() =>
                                  history.push(`/campaign/${campaign.id}/report`)
                                }
                                size="small"
                              >
                                <DescriptionIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={i18n.t("campaigns.tooltips.duplicate") || "Duplicar"}>
                              <IconButton
                                size="small"
                                onClick={() => handleDuplicateCampaign(campaign)}
                              >
                                <FileCopyIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={i18n.t("campaigns.tooltips.edit")}>
                              <IconButton
                                size="small"
                                onClick={() => handleEditCampaign(campaign)}
                              >
                                <EditIcon style={{ color: "#00a8ff" }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={i18n.t("campaigns.tooltips.delete")}>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  setConfirmModalOpen(true);
                                  setDeletingCampaign(campaign);
                                }}
                              >
                                <DeleteOutlineIcon style={{ color: "#e84118" }} />
                              </IconButton>
                            </Tooltip>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {loading && <TableRowSkeleton columns={10} />}
                  </>
                </TableBody>
              </Table>
              </TableContainer>
              {/* Paginação */}
              <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
                <Box display="flex" alignItems="center" gap={2}>
                  <Box color="text.secondary" fontSize="0.875rem">
                    {i18n.t("campaigns.table.total", { count: totalCount })}
                  </Box>
                </Box>

                <TablePagination
                  component="div"
                  count={totalCount}
                  page={pageNumber - 1} // Material-UI usa índice baseado em 0
                  onPageChange={handlePageChange}
                  rowsPerPage={pageSize}
                  onRowsPerPageChange={handlePageSizeChange}
                  rowsPerPageOptions={[5, 10, 25, 50, 100]}
                  labelRowsPerPage={i18n.t("campaigns.pagination.itemsPerPage")}
                  labelDisplayedRows={({ from, to, count }) => {
                    const ofText = i18n.language === 'en' ? 'of' : 'de';
                    const moreText = i18n.language === 'en' ? 'more than' : 'más de';
                    return `${from}-${to} ${ofText} ${count !== -1 ? count : `${moreText} ${to}`}`;
                  }}
                />
              </Box>

            </Paper>
          </>
      }
    </MainContainer>
  );
};

export default Campaigns;
